import type { TenantCompanyProfile, DunningLevelSettings } from '@forq/platform-data';
import type { Invoice, InvoiceDunning } from '@forq/finance-data';

/**
 * Der Mahnbrief -- bis 09/2026 gab es ihn nicht. „Mahnen" erhöhte einen Zähler; der Kunde bekam
 * nie etwas zu sehen, und eine Mahnung, die niemand zugeht, setzt niemanden in Verzug.
 *
 * Gerendert aus dem EINGEFRORENEN Mahnvorgang, nicht aus der aktuellen Rechnung: Was gemahnt
 * wurde, muss auch dann noch nachvollziehbar sein, wenn inzwischen eine Teilzahlung eingegangen
 * ist. Derselbe Briefkopf und dieselbe Fußzeile wie alle anderen FORQ-Belege.
 *
 * Gebühr und Zinsen stehen im Brief, erhöhen aber NICHT die Forderung der Rechnung -- eine
 * festgeschriebene Rechnung nachträglich zu erhöhen wäre GoBD-widrig.
 */

const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
function money(value: number): string { return euro.format(value); }
function dateLabel(value: string | null | undefined): string {
  const [year, month, day] = (value || '').slice(0, 10).split('-');
  return year && month && day ? `${day}.${month}.${year}` : '–';
}
function esc(value: string): string {
  return (value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

export interface DunningRecipient {
  readonly name: string;
  readonly street: string;
  readonly postalCode: string;
  readonly city: string;
  readonly countryCode: string;
}

export interface DunningDocumentContext {
  readonly invoice: Invoice;
  /** Der Mahnvorgang, aus dem der Brief entsteht -- mit eingefrorenen Beträgen. */
  readonly dunning: InvoiceDunning;
  /** Die Stufe aus dem Firmenprofil: Bezeichnung und Brieftext. */
  readonly level: DunningLevelSettings;
  readonly company: TenantCompanyProfile;
  readonly recipient: DunningRecipient;
}

/** Platzhalter im Stufentext ersetzen -- dieselbe Sprache wie in den Belegtexten des Profils. */
function fillText(text: string, invoice: Invoice, dunning: InvoiceDunning): string {
  return text
    .replaceAll('{nummer}', invoice.invoiceNumber ?? invoice.proposalNumber)
    .replaceAll('{betrag}', money(dunning.openAmount))
    .replaceAll('{fällig}', dateLabel(dunning.dueDate))
    .replaceAll('{faellig}', dateLabel(dunning.dueDate));
}

export function renderDunningHtml(ctx: DunningDocumentContext): string {
  const { invoice, dunning, level, company, recipient } = ctx;
  const accent = company.letterhead.accentColor || '#e30613';
  const nummer = invoice.invoiceNumber ?? invoice.proposalNumber;
  const gesamt = Math.round((dunning.openAmount + dunning.fee + dunning.interest) * 100) / 100;

  const empfaenger = [
    recipient.name,
    recipient.street,
    `${recipient.postalCode} ${recipient.city}`.trim(),
    recipient.countryCode && recipient.countryCode !== 'DE' ? recipient.countryCode : '',
  ].filter(Boolean).map((zeile) => esc(zeile)).join('<br>');

  const logo = company.letterhead.logoDataUrl
    ? `<img src="${esc(company.letterhead.logoDataUrl)}" alt="${esc(company.identity.name)}" style="max-height:22mm">`
    : `<div style="font-size:20px;font-weight:800">${esc(company.identity.name || 'FORQ')}</div>`;

  const fuss = company.letterhead.footerColumns
    .filter((spalte) => spalte.trim())
    .map((spalte) => `<div>${esc(spalte)}</div>`)
    .join('');

  // Die Aufstellung: die gemahnte Forderung, dann was hinzukommt.
  const zeilen = [
    `<tr><td>Rechnung ${esc(nummer)} vom ${dateLabel(invoice.issueDate)}</td>`
    + `<td class="num">${dateLabel(invoice.dueDate)}</td>`
    + `<td class="num">${money(dunning.openAmount)}</td></tr>`,
    dunning.fee > 0
      ? `<tr><td>Mahngebühr</td><td class="num">–</td><td class="num">${money(dunning.fee)}</td></tr>`
      : '',
    dunning.interest > 0
      ? `<tr><td>Verzugszinsen</td><td class="num">–</td><td class="num">${money(dunning.interest)}</td></tr>`
      : '',
  ].join('');

  return `<!doctype html><html lang="de"><head><meta charset="utf-8">
<title>${esc(level.label)} ${esc(nummer)}</title>
<style>
  :root { --accent: ${esc(accent)}; }
  * { box-sizing: border-box; }
  body { margin: 0; background: #f2f2f2; font-family: "Segoe UI", Arial, sans-serif; color: #222; }
  .toolbar { padding: 8px; text-align: right; }
  .toolbar button { font: inherit; padding: 6px 14px; cursor: pointer; }
  .sheet { width: 210mm; min-height: 297mm; margin: 0 auto 10mm; padding: ${company.letterhead.marginTopMm}mm ${company.letterhead.marginRightMm}mm ${company.letterhead.marginBottomMm}mm ${company.letterhead.marginLeftMm}mm; background: #fff; box-shadow: 0 1px 8px rgba(0,0,0,.15); }
  .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 10mm; }
  .band { display: flex; justify-content: space-between; gap: 10mm; margin-top: 14mm; }
  .sender { font-size: 7.5px; color: #666; border-bottom: .5px solid #bbb; padding-bottom: 2px; margin-bottom: 3px; }
  .to { font-size: 12px; line-height: 1.5; }
  .doc-head { min-width: 74mm; border-left: 3px solid var(--accent); padding-left: 10px; }
  .doc-title { font-size: 20px; font-weight: 800; margin-bottom: 6px; color: #111; }
  table.meta { border-collapse: collapse; font-size: 11px; }
  table.meta th { text-align: left; color: #444; font-weight: 500; padding: 1px 14px 1px 0; }
  table.meta td { font-weight: 700; }
  .intro { margin: 8mm 0 3mm; text-align: justify; }
  table.pos { width: 100%; border-collapse: collapse; margin-top: 4mm; font-size: 11px; }
  table.pos th { text-align: left; color: #444; font-weight: 700; padding: 6px 7px; border-bottom: 1.5px solid var(--accent); background: #fafafa; }
  table.pos td { padding: 5px 7px; border-bottom: 1px solid #e6e6e6; }
  table.pos .num { text-align: right; white-space: nowrap; }
  table.totals { margin: 4mm 0 0 auto; border-collapse: collapse; min-width: 92mm; font-size: 12px; }
  table.totals th { text-align: left; color: #444; font-weight: 500; padding: 3px 12px 3px 0; }
  table.totals td { text-align: right; font-weight: 800; padding: 3px 0; }
  table.totals tr.is-strong th, table.totals tr.is-strong td { border-top: 1.5px solid var(--accent); font-size: 13.5px; color: #111; padding-top: 5px; }
  .note { font-size: 10.5px; color: #444; margin: 6mm 0 0; text-align: justify; }
  .footer { margin-top: 14mm; border-top: 1.5px solid var(--accent); padding-top: 5px; display: flex; gap: 8mm; }
  .footer div { flex: 1; font-size: 8px; color: #555; white-space: pre-line; line-height: 1.4; }
  @media print {
    body { background: #fff; }
    .toolbar { display: none; }
    .sheet { margin: 0; box-shadow: none; width: auto; padding: 0; }
    @page { size: A4; margin: 12mm 14mm; }
  }
</style></head>
<body>
  <div class="toolbar"><button type="button" onclick="window.print()">Drucken / als PDF speichern</button></div>
  <div class="sheet">
    <div class="head">${logo}</div>
    <div class="band">
      <div>
        <div class="sender">${esc(company.identity.name || '')}${company.identity.address.street ? ` · ${esc(company.identity.address.street)} · ${esc(`${company.identity.address.postalCode} ${company.identity.address.city}`.trim())}` : ''}</div>
        <div class="to">${empfaenger}</div>
      </div>
      <div class="doc-head">
        <div class="doc-title">${esc(level.label.toUpperCase())}</div>
        <table class="meta">
          <tr><th>Rechnungs-Nr.:</th><td>${esc(nummer)}</td></tr>
          <tr><th>Datum:</th><td>${dateLabel(dunning.issuedDate)}</td></tr>
          <tr><th>Kunden-Nr.:</th><td>${esc(invoice.customerNumber)}</td></tr>
        </table>
      </div>
    </div>
    <p class="intro">Sehr geehrte Damen und Herren,<br>${esc(fillText(level.text, invoice, dunning))}</p>
    <table class="pos">
      <thead><tr><th>Beleg</th><th class="num">fällig am</th><th class="num">Betrag</th></tr></thead>
      <tbody>${zeilen}</tbody>
    </table>
    <table class="totals">
      <tr class="is-strong"><th>Zu zahlen bis ${dateLabel(dunning.dueDate)}</th><td>${money(gesamt)}</td></tr>
    </table>
    ${company.bank.iban ? `<p class="note">Bitte überweisen Sie den Betrag auf: ${esc(company.bank.bankName)} · IBAN ${esc(company.bank.iban)}${company.bank.bic ? ` · BIC ${esc(company.bank.bic)}` : ''}. Bitte geben Sie dabei die Rechnungsnummer ${esc(nummer)} an.</p>` : ''}
    ${dunning.note ? `<p class="note">${esc(dunning.note)}</p>` : ''}
    <div class="footer">${fuss}</div>
  </div>
</body></html>`;
}
